import { Component, Inject, OnInit } from '@angular/core';
import { FormArray, AbstractControl, ValidationErrors, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { FebStatut, IFicheExpressionBesoinRequest, IFicheExpressionBesoinResponse } from 'src/app/shared/models/fiche-expression-besoin';
import { FicheExpressionBesoinService } from '../fiche-expression-besoin.service';
import { FebLocationsService } from 'src/app/core/feb-locations-management/feb-locations.service';
import { FebActiviteUoNomenclatureService } from 'src/app/core/feb-activite-uo-nomenclature-management/feb-activite-uo-nomenclature.service';
import { UsersService } from 'src/app/core/user/services/users.service';
import { DomainesService } from 'src/app/core/domaines/domaines.service';
import { ApplicationProjectService } from 'src/app/core/application-project/services/application-project.service';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { IUser } from 'src/app/shared/models/user';
import { UserProfile } from 'src/app/shared/models/user-profile';
import { DsiTypes, IDsi } from 'src/app/shared/models/dsi';
import { IProject } from 'src/app/shared/models/project';
import { IFebLocalisation } from 'src/app/shared/models/feb-localisation';
import { IFebActiviteUoNomenclature } from 'src/app/shared/models/feb-activite-uo-nomenclature';
import { FebActiviteUoNomenclatureDialogComponent } from 'src/app/core/feb-activite-uo-nomenclature-management/feb-activite-uo-nomenclature-dialog/feb-activite-uo-nomenclature-dialog.component';

@Component({
  selector: 'app-fiche-expression-besoin-dialog',
  templateUrl: './fiche-expression-besoin-dialog.component.html',
  styleUrls: ['./fiche-expression-besoin-dialog.component.less']
})
export class FicheExpressionBesoinDialogComponent implements OnInit {

  isEditMode: boolean;
  isReadOnly: boolean;

  step1Form!: UntypedFormGroup;
  step2Form!: UntypedFormGroup;
  step3Form!: UntypedFormGroup;

  users: IUser[] = [];
  pilots: IUser[] = [];
  cdsEntities: IDsi[] = [];
  dsiDivisions: IDsi[] = [];
  projects: IProject[] = [];
  localisations: IFebLocalisation[] = [];
  nomenclatures: IFebActiviteUoNomenclature[] = [];

  backendError: string | null = null;
  isSubmitting = false;

  filteredPilots!: Observable<IUser[]>;
  filteredDemandeurs!: Observable<IUser[]>;
  filteredNomenclatures!: Observable<IFebActiviteUoNomenclature[]>;

  constructor(
    private _fb: UntypedFormBuilder,
    private febService: FicheExpressionBesoinService,
    private febLocationsService: FebLocationsService,
    private febActiviteService: FebActiviteUoNomenclatureService,
    private usersService: UsersService,
    private domainesService: DomainesService,
    private appProjectService: ApplicationProjectService,
    public authenticationService: AuthenticationService,
    private snackbarService: SnackbarService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<FicheExpressionBesoinDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IFicheExpressionBesoinResponse | null
  ) {
    this.isEditMode = data != null;
    this.isReadOnly = this.isEditMode
      && data?.febStatut != null
      && data.febStatut !== FebStatut.CREEE
      && !authenticationService.isUserAdmin();

    this.buildForms();
  }

  // ── Construction des formulaires ─────────────────────────────────────────

  private buildForms(): void {
    this.step1Form = this._fb.group({
      febLabel:               [this.data?.febLabel ?? '', [Validators.required, Validators.maxLength(100)]],
      febPilotLotId:          [this.data?.febPilotLotId ?? null, Validators.required],
      febPilotLotSearch:      [this.data?.febPilotLotNom ?? ''],
      febDemandeurId:         [this.data?.febDemandeurId ?? '', Validators.required],
      febDemandeurSearch:     [this.data?.febDemandeurNom ?? ''],
      febEntiteSncfId:        [this.data?.febEntiteSncfId ?? null, Validators.required],
      febDivisionDemandeurId: [this.data?.febDivisionDemandeurId ?? null, Validators.required],
      febProjectId:           [this.data?.febProjectId ?? null, Validators.required],
      febPcSncf:              [this.data?.febPcSncf ?? false],
      febDateDemande:         [this.data?.febDateDemande ? new Date(this.data.febDateDemande) : null, Validators.required],
      febDateDemarrage:       [this.data?.febDateDemarrage ? new Date(this.data.febDateDemarrage) : null, Validators.required],
      febLocalisationId:      [this.data?.febLocalisationId ?? null, Validators.required],
      febDuree:               [this.data?.febDuree ?? null, [Validators.required, Validators.min(0), Validators.pattern(/^\d+(\.\d{1,1})?$/)]],
      febDateFinPrevisionnelle:[this.data?.febDateFinPrevisionnelle ? new Date(this.data.febDateFinPrevisionnelle) : null, Validators.required],
    }, { validators: FicheExpressionBesoinDialogComponent.dateRangeValidator });

    this.step2Form = this._fb.group({
      febUoActivitePressentiId:          [this.data?.febUoActivitePressentiId ?? null, Validators.required],
      febNomenclatureSearch: [
        this.data?.febUoActivitePressentiLabel
          ? this.data.febUoActivitePressentiLabel + (this.data.febUoNomenclatureLabel ? ' — ' + this.data.febUoNomenclatureLabel : '')
          : ''
      ],
      febNomenclature:                   [this.data?.febNomenclature ?? '', Validators.required],
      febPresenceSiteSncf:               [this.data?.febPresenceSiteSncf ?? '', [Validators.required, Validators.maxLength(50)]],
      febProvisionDeplacementAstreinte:  [this.data?.febProvisionDeplacementAstreinte ?? 0, Validators.required],
      febContexteObjectifProjet:         [this.data?.febContexteObjectifProjet ?? '', Validators.required],
      febDescriptionBesoin:              [this.data?.febDescriptionBesoin ?? '', Validators.required],
    });

    const activitesControls = (this.data?.febActivites ?? []).map(a =>
      this._fb.group({
        label:       [a.label, Validators.required],
        pourcentage: [a.pourcentage, [Validators.required, Validators.min(0), Validators.max(100)]]
      })
    );

    this.step3Form = this._fb.group({
      activites:                   this._fb.array(activitesControls),
      febNbAnneesExperiences:      [this.data?.febNbAnneesExperiences ?? '', Validators.required],
      febCompetencesFonctionnelles:[this.data?.febCompetencesFonctionnelles ?? '', Validators.required],
      febCompetencesTechniques:    [this.data?.febCompetencesTechniques ?? '', Validators.required],
      febEnvironnementTechnique:   [this.data?.febEnvironnementTechnique ?? '', Validators.required],
      febSavoirEtreQualites:       [this.data?.febSavoirEtreQualites ?? '', Validators.required],
    }, { validators: FicheExpressionBesoinDialogComponent.activitesTotalValidator });
  }

  // ── Getters ───────────────────────────────────────────────────────────────

  get readonlyMessage(): string {
    switch (this.data?.febStatut) {
      case FebStatut.EN_ATTENTE: return 'Cette fiche est en attente de validation par le CDS — consultation uniquement.';
      case FebStatut.VALIDEE:    return 'Cette fiche a été validée par le CDS — consultation uniquement.';
      case FebStatut.ARCHIVEE:   return 'Cette fiche est archivée — consultation uniquement.';
      default:                   return 'Cette fiche est en lecture seule — modification impossible.';
    }
  }

  get activitesArray(): FormArray {
    return this.step3Form.get('activites') as FormArray;
  }

  get activitesTotal(): number {
    return this.activitesArray.controls.reduce(
      (sum, ctrl) => sum + (+(ctrl.get('pourcentage')?.value) || 0), 0);
  }

  get hasDateWarning(): boolean {
    const demande   = this.step1Form.get('febDateDemande')?.value;
    const demarrage = this.step1Form.get('febDateDemarrage')?.value;
    return demande && demarrage ? new Date(demande) > new Date(demarrage) : false;
  }

  // ── Validateurs statiques ─────────────────────────────────────────────────

  static dateRangeValidator(control: AbstractControl): ValidationErrors | null {
    const demarrage = control.get('febDateDemarrage')?.value;
    const fin       = control.get('febDateFinPrevisionnelle')?.value;
    if (demarrage && fin && new Date(demarrage) > new Date(fin)) {
      return { dateRange: true };
    }
    return null;
  }

  static activitesTotalValidator(control: AbstractControl): ValidationErrors | null {
    const arr = control.get('activites') as FormArray;
    if (!arr || arr.length === 0) return { activitesRequired: true };
    const total = arr.controls.reduce(
      (sum, c) => sum + (+(c.get('pourcentage')?.value) || 0), 0);
    return total === 100 ? null : { activitesTotalNot100: { actual: total } };
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.loadReferenceData();
    if (this.isReadOnly) {
      this.step1Form.disable();
      this.step2Form.disable();
      this.step3Form.disable();
    }
  }

  // ── Chargement des référentiels ───────────────────────────────────────────

  loadReferenceData(): void {
  this.usersService.getAllContacts().subscribe({
    next: users => {
      this.pilots = users.filter(u => u.profile === UserProfile.SERVICE_MANAGER);
      this.users  = users.filter(u => u.profile === UserProfile.CP_SNCF);
      this.setupUserAutocomplete();
    }
  });

  this.domainesService.getDsis().subscribe({
    next: dsis => {
      this.cdsEntities  = dsis.filter(d => d.type === DsiTypes.CDS);
      this.dsiDivisions = dsis.filter(d => d.type === DsiTypes.DSI);
    }
  });

  this.appProjectService.getProjects().subscribe({
  next: projects => {
    const isAdminOrManager = this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS();

    if (isAdminOrManager) {
      this.projects = projects;
    } else {
      const currentUser = this.authenticationService.getUser();

      const userApplications = currentUser?.applications ?? [];
      const userAppNames = new Set(
        userApplications
          .map(app => app?.name)
          .filter((name: string | undefined): name is string => typeof name === 'string' && name.trim().length > 0)
          .map(name => name.trim().toLowerCase())
      );

      if (userAppNames.size === 0) {
        this.projects = [];
        return;
      }

      this.appProjectService.getApplications().subscribe({
        next: applications => {
          const resolvedProjectIds = new Set(
            (applications ?? [])
              .filter(app => {
                const appName = app?.name?.trim().toLowerCase();
                return !!appName && userAppNames.has(appName);
              })
              .map(app => app?.project?.id)
              .filter((id: string | undefined): id is string => typeof id === 'string' && id.trim().length > 0)
          );

          this.projects = projects.filter(project => {
            const projectId = project?.id;
            return !!projectId && resolvedProjectIds.has(projectId);
          });
        },
        error: () => {
          this.projects = [];
        }
      });
    }
  }
});

  this.febLocationsService.getAll().subscribe({ next: locs => { this.localisations = locs; } });
  this.febActiviteService.getAll().subscribe({
    next: noms => {
      this.nomenclatures = noms;
      this.setupNomenclatureAutocomplete();
    }
  });
}

  // ── Autocomplete ──────────────────────────────────────────────────────────

  setupUserAutocomplete(): void {
    this.filteredPilots = this.step1Form.get('febPilotLotSearch')!.valueChanges.pipe(
      startWith(''), map(val => this.filterPilots(val ?? ''))
    );
    this.filteredDemandeurs = this.step1Form.get('febDemandeurSearch')!.valueChanges.pipe(
      startWith(''), map(val => this.filterUsers(val ?? ''))
    );
  }

  setupNomenclatureAutocomplete(): void {
    this.filteredNomenclatures = this.step2Form.get('febNomenclatureSearch')!.valueChanges.pipe(
      startWith(''), map(val => this.filterNomenclatures(val ?? ''))
    );
  }

  filterPilots(val: string): IUser[] {
    if (!val) return this.pilots;
    const lower = val.toLowerCase();
    return this.pilots.filter(u =>
      ((u.firstName ?? '') + ' ' + (u.lastName ?? '')).toLowerCase().includes(lower));
  }

  filterUsers(val: string): IUser[] {
    if (!val) return this.users;
    const lower = val.toLowerCase();
    return this.users.filter(u =>
      ((u.firstName ?? '') + ' ' + (u.lastName ?? '')).toLowerCase().includes(lower));
  }

  filterNomenclatures(val: string): IFebActiviteUoNomenclature[] {
    if (!val) return this.nomenclatures;
    const lower = val.toLowerCase();
    return this.nomenclatures.filter(n =>
      ((n.activiteUOoperationnelle ?? '') + ' ' + (n.nomenclatureLabel ?? '')).toLowerCase().includes(lower));
  }

  selectPilot(user: IUser): void {
    this.step1Form.get('febPilotLotId')!.setValue(user.id ?? null);
    this.step1Form.get('febPilotLotSearch')!.setValue(
      (user.firstName ?? '') + ' ' + (user.lastName ?? ''), { emitEvent: false });
  }

  selectDemandeur(user: IUser): void {
    this.step1Form.get('febDemandeurId')!.setValue(user.id ?? '');
    this.step1Form.get('febDemandeurSearch')!.setValue(
      (user.firstName ?? '') + ' ' + (user.lastName ?? ''), { emitEvent: false });
  }

  selectNomenclature(n: IFebActiviteUoNomenclature): void {
    this.step2Form.get('febUoActivitePressentiId')!.setValue(n.nomenclatureId ?? n.id ?? null);
    this.step2Form.get('febNomenclature')!.setValue(n.nomenclatureLabel ?? '');
    this.step2Form.get('febNomenclatureSearch')!.setValue(
      (n.activiteUOoperationnelle ?? '') + ' — ' + (n.nomenclatureLabel ?? ''),
      { emitEvent: false });
  }

  // ── Activités ─────────────────────────────────────────────────────────────

  addActivite(): void {
    this.activitesArray.push(this._fb.group({
      label:       ['', Validators.required],
      pourcentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]]
    }));
  }

  removeActivite(index: number): void {
    this.activitesArray.removeAt(index);
  }

  openNomenclatureDialog(): void {
    const ref = this.dialog.open(FebActiviteUoNomenclatureDialogComponent, {
      width: '600px', data: null
    });
    ref.afterClosed().subscribe((result: IFebActiviteUoNomenclature | undefined) => {
      if (result) {
        this.nomenclatures = [...this.nomenclatures, result];
        this.setupNomenclatureAutocomplete();
        this.selectNomenclature(result);
      }
    });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  formatDate(d: Date | string | null): string | undefined {
    if (!d) return undefined;
    return new Date(d).toISOString().split('T')[0];
  }

  /**
   * Extrait le message d'erreur depuis la réponse HTTP.
   * Le GlobalExceptionHandler renvoie { status, code, message, ... }
   * On affiche `message` en priorité, sinon un message générique.
   */
  private extractErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    console.log(body)
    if (body?.message && typeof body.message === 'string') return body.message;
    if (typeof body === 'string') return body;
    return fallback;
  }

  // ── Soumission ────────────────────────────────────────────────────────────

  onSubmit(): void {
    if (this.step1Form.invalid) {
      this.step1Form.markAllAsTouched();
      return;
    }

    this.backendError = null;
    this.isSubmitting = true;

    const s1 = this.step1Form.value;
    const s2 = this.step2Form.value;
    const s3 = this.step3Form.value;

    const request: IFicheExpressionBesoinRequest = {
      febLabel:                        s1.febLabel,
      febDemandeurId:                  s1.febDemandeurId,
      febPilotLotId:                   s1.febPilotLotId || undefined,
      febEntiteSncfId:                 s1.febEntiteSncfId || undefined,
      febDivisionDemandeurId:          s1.febDivisionDemandeurId || undefined,
      febProjectId:                    s1.febProjectId || undefined,
      febPcSncf:                       s1.febPcSncf ?? false,
      febDateDemande:                  this.formatDate(s1.febDateDemande),
      febDateDemarrage:                this.formatDate(s1.febDateDemarrage),
      febLocalisationId:               s1.febLocalisationId || undefined,
      febDuree:                        s1.febDuree ?? undefined,
      febDateFinPrevisionnelle:        this.formatDate(s1.febDateFinPrevisionnelle),
      febUoActivitePressentiId:        s2.febUoActivitePressentiId || undefined,
      febNomenclature:                 s2.febNomenclature || undefined,
      febPresenceSiteSncf:             s2.febPresenceSiteSncf || undefined,
      febProvisionDeplacementAstreinte:s2.febProvisionDeplacementAstreinte || 0,
      febContexteObjectifProjet:       s2.febContexteObjectifProjet || undefined,
      febDescriptionBesoin:            s2.febDescriptionBesoin || undefined,
      febActivites: (s3.activites ?? []).map((a: { label: string; pourcentage: number }) => ({
        label: a.label, pourcentage: a.pourcentage
      })),
      febNbAnneesExperiences:       s3.febNbAnneesExperiences || undefined,
      febCompetencesFonctionnelles: s3.febCompetencesFonctionnelles || undefined,
      febCompetencesTechniques:     s3.febCompetencesTechniques || undefined,
      febEnvironnementTechnique:    s3.febEnvironnementTechnique || undefined,
      febSavoirEtreQualites:        s3.febSavoirEtreQualites || undefined,
      febCreatedById:               this.authenticationService.getUser()?.id,
    };

    const action$ = !this.isEditMode
      ? this.febService.create(request)
      : this.febService.update(this.data!.febId, request);

    const successMsg = !this.isEditMode ? 'Fiche créée avec succès' : 'Fiche modifiée avec succès';
    const fallbackMsg = !this.isEditMode
      ? 'Erreur lors de la création de la fiche.'
      : 'Erreur lors de la modification de la fiche.';

    action$.subscribe({
      next: result => {
        this.isSubmitting = false;
        this.snackbarService.openSnackBar(successMsg);
        this.dialogRef.close(result);
      },
      error: err => {
        this.isSubmitting = false;
        this.backendError = this.extractErrorMessage(err, fallbackMsg);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}